import { ChangeDetectionStrategy, Component } from '@angular/core';
import { AnimationBehavior } from '@freelancer/directives';
import { Feature } from '@freelancer/feature-flags';
import { ContainerSize } from '@freelancer/ui/container';
import { VerticalAlignment } from '@freelancer/ui/grid';
import { IconColor } from '@freelancer/ui/icon';
import {
  LinkColor,
  LinkHoverColor,
  LinkIconPosition,
  LinkWeight,
} from '@freelancer/ui/link';
import type { PictureBackgroundImageStyles } from '@freelancer/ui/picture';
import { PictureBgImageObjectFit } from '@freelancer/ui/picture';
import { TextSize } from '@freelancer/ui/text';

@Component({
  standalone: false,
  selector: 'app-home-page-api-enterprise-v5',
  template: `
    <fl-container
      class="AnimationContainer"
      [size]="ContainerSize.DESKTOP_LARGE"
      [sizeDesktopXLarge]="ContainerSize.DESKTOP_XLARGE"
    >
      <div
        class="AnimatedLineTop"
        [flAnimateOnScroll]="true"
        [flAnimateOnScrollBehavior]="AnimationBehavior.GROW"
        [flAnimateOnScrollDelay]="0.5"
        [flAnimateOnScrollDuration]="0.3"
      ></div>
      <div
        class="AnimatedLogo"
        [flAnimateOnScroll]="true"
        [flAnimateOnScrollBehavior]="AnimationBehavior.FADE"
        [flAnimateOnScrollDuration]="0.2"
        [flAnimateOnScrollDelay]="0.8"
      ></div>
      <div
        class="AnimatedLineBottom"
        [flAnimateOnScroll]="true"
        [flAnimateOnScrollBehavior]="AnimationBehavior.GROW"
        [flAnimateOnScrollDelay]="0.8"
        [flAnimateOnScrollDuration]="0.5"
      ></div>
    </fl-container>
    <fl-container
      class="Container"
      [size]="ContainerSize.DESKTOP_LARGE"
      [sizeDesktopXLarge]="ContainerSize.DESKTOP_XLARGE"
    >
      <fl-picture
        class="mobile:hide"
        [isBackgroundImage]="true"
        [srcTablet]="'home/api-enterprise/enterprise-upsell-v5-tablet.jpg'"
        [srcDesktop]="
          'home/api-enterprise/enterprise-upsell-v5-desktop-test.jpg'
        "
        [backgroundStyles]="backgroundStyles"
      >
      </fl-picture>
      <div class="Wrapper">
        <fl-grid [vAlign]="VerticalAlignment.VERTICAL_CENTER">
          <fl-col
            [col]="12"
            [colTablet]="8"
            [colDesktopSmall]="6"
            [flAnimateOnScroll]="true"
            [flAnimateOnScrollBehavior]="AnimationBehavior.FADE"
            [flAnimateOnScrollDuration]="0.3"
          >
            <h3
              class="text-light text-title-40 desktop:text-title-60 mb-mid tablet:mb-large desktop:mb-xlarge"
              i18n="Hire categories blade heading"
            >
              Power your organisation's
              <div class="Heading">competitive advantage</div>
            </h3>
          </fl-col>
        </fl-grid>
        <fl-grid>
          <fl-col
            class="mb-large tablet:mb-none"
            [col]="12"
            [colTablet]="8"
            [colDesktopSmall]="6"
            [flAnimateOnScroll]="true"
            [flAnimateOnScrollBehavior]="AnimationBehavior.FADE"
            [flAnimateOnScrollDuration]="0.3"
            [flAnimateOnScrollDelay]="0.1"
          >
            <p
              class="text-light text-body-20 desktop:text-title-32 font-bold mb-xsmall"
              i18n="Freelancer Enterprise Blade Header"
            >
              Freelancer Enterprise
            </p>
            <p
              class="text-light text-body-16 desktop:text-body-20 mb-xxsmall"
              i18n="Freelancer Enterprise Blade Description"
            >
              Company budget? Get more done for less. Use our workforce of
              millions to help your business achieve more.
            </p>
            <fl-link
              class="ViewMore mb-large tablet:mb-xlarge"
              i18n="Link for the Freelancer Enterprise blade"
              [flTrackingLabel]="'HireCategories-more'"
              [color]="LinkColor.PRIMARY"
              [iconColor]="IconColor.PRIMARY"
              [hoverColor]="LinkHoverColor.PRIMARY"
              [iconName]="'ui-arrow-right'"
              [iconPosition]="LinkIconPosition.RIGHT"
              [link]="'/enterprise'"
              [size]="TextSize.BODY_16"
              [weight]="LinkWeight.BOLD"
            >
              View more
            </fl-link>

            <div *flFeature="Feature.INNOVATION_CHALLENGES">
              <p
                class="text-light text-body-20 desktop:text-title-32 font-bold mb-xsmall"
                i18n="Innovation Challenges Blade Header"
              >
                Innovation Challenges
              </p>
              <p
                class="text-light text-body-16 desktop:text-body-20 mb-xxsmall"
                i18n="Innovation Challenges Blade Description"
              >
                Transform impossible challenges into breakthrough solutions
                through the world's largest innovation ecosystem.
              </p>
              <fl-link
                class="ViewMore mb-large tablet:mb-xlarge"
                i18n="Link for the Innovation Challenges blade"
                [flTrackingLabel]="'InnovationChallenges-more'"
                [color]="LinkColor.PRIMARY"
                [iconColor]="IconColor.PRIMARY"
                [hoverColor]="LinkHoverColor.PRIMARY"
                [iconName]="'ui-arrow-right'"
                [iconPosition]="LinkIconPosition.RIGHT"
                [link]="'/innovation'"
                [size]="TextSize.BODY_16"
                [weight]="LinkWeight.BOLD"
              >
                View more
              </fl-link>
            </div>

            <p
              class="text-light text-body-20 desktop:text-title-32 font-bold mb-xsmall"
              i18n="Freelancer API Blade Header"
            >
              Freelancer API
            </p>
            <p
              class="text-light text-body-16 desktop:text-body-20 mb-xxsmall"
              i18n="Freelancer API Blade description"
            >
              Millions of professionals on demand. Why hire people when you can
              simply integrate our talented cloud workforce instead?
            </p>
            <fl-link
              i18n="View Documentation link for the Freelancer API blade"
              [flTrackingLabel]="'HireCategories-more'"
              [color]="LinkColor.PRIMARY"
              [iconColor]="IconColor.PRIMARY"
              [hoverColor]="LinkHoverColor.PRIMARY"
              [iconName]="'ui-arrow-right'"
              [iconPosition]="LinkIconPosition.RIGHT"
              [link]="'https://developers.freelancer.com/'"
              [size]="TextSize.BODY_16"
              [weight]="LinkWeight.BOLD"
            >
              View more
            </fl-link>
          </fl-col>
        </fl-grid>
      </div>
    </fl-container>
  `,
  styleUrls: ['./api-enterprise-v5.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomePageApiEnterpriseV5Component {
  AnimationBehavior = AnimationBehavior;
  ContainerSize = ContainerSize;
  Feature = Feature;
  LinkHoverColor = LinkHoverColor;
  IconColor = IconColor;
  LinkColor = LinkColor;
  LinkIconPosition = LinkIconPosition;
  LinkWeight = LinkWeight;
  TextSize = TextSize;
  VerticalAlignment = VerticalAlignment;

  readonly backgroundStyles: PictureBackgroundImageStyles = {
    position: 'center top',
    positionDesktop: 'center',
    objectFitTablet: PictureBgImageObjectFit.NONE,
  };
}
